import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useProject } from '../../../contexts/ProjectContext';
import brandConfig from '../../../config/brandConfig';

const DraftProjectModal = ({ isOpen, onClose, projectName }) => {
  const { project, projectId } = useProject();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div 
        className="bg-white rounded-lg max-w-md w-full shadow-theme-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                <FontAwesomeIcon icon={faInfoCircle} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Proyecto en Construcción</h2>
                <p className="text-sm text-gray-600 mt-0.5">Estado: Edición</p>
              </div>
            </div>
            {/* No mostrar botón de cerrar cuando está en EDICION - Modal no cerrable */}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              El proyecto <strong className="font-semibold text-gray-900">{projectName || 'este proyecto'}</strong> está actualmente en <strong className="font-semibold text-amber-700">modo edición/borrador</strong> y se encuentra en construcción interna.
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-600 text-xs font-bold">⚙️</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Configuración en Progreso</p>
                <p className="text-xs text-gray-600 mt-1">Estamos configurando precios, lotes y mapas para ofrecerte la mejor experiencia.</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-xs font-bold">🚀</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Próximamente Disponible</p>
                <p className="text-xs text-gray-600 mt-1">El proyecto será publicado muy pronto para que puedas explorar todos los lotes disponibles.</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-600 text-xs font-bold">📧</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Mantente Informado</p>
                <p className="text-xs text-gray-600 mt-1">Te notificaremos cuando el proyecto esté disponible para consulta pública.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer con Formulario */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
          {!showForm && !isSuccess ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold flex items-center justify-center space-x-2"
            >
              <span>📝</span>
              <span>Dejar mis datos para notificación</span>
            </button>
          ) : isSuccess ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <span className="text-3xl">✅</span>
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1">
                ¡Registro exitoso!
              </p>
              <p className="text-xs text-gray-600">
                Te notificaremos por correo cuando el proyecto esté disponible.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <input
                    type="text"
                    placeholder="Nombre *"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="text-[10px] text-red-600 mt-0.5">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Apellido"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="text-[10px] text-red-600 mt-0.5">{errors.lastName}</p>
                  )}
                </div>
              </div>
              
              <div>
                <input
                  type="tel"
                  placeholder="Teléfono *"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="text-[10px] text-red-600 mt-0.5">{errors.phone}</p>
                )}
              </div>
              
              <div>
                <input
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="text-[10px] text-red-600 mt-0.5">{errors.email}</p>
                )}
              </div>
              
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptsTerms}
                  onChange={(e) => setAcceptsTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <label htmlFor="acceptTerms" className="text-[10px] text-gray-700">
                  Acepto los <a href="/terminos" target="_blank" className="text-green-600 hover:underline">términos y condiciones</a> *
                </label>
              </div>
              {errors.acceptsTerms && (
                <p className="text-[10px] text-red-600">{errors.acceptsTerms}</p>
              )}
              
              {errors.submit && (
                <p className="text-[10px] text-red-600 text-center">{errors.submit}</p>
              )}
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ firstName: '', lastName: '', phone: '', email: '' });
                    setErrors({});
                    setAcceptsTerms(false);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-xs font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  // Validar formulario
  function validateForm() {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!/^[0-9+\s()-]*$/.test(formData.phone)) {
      newErrors.phone = 'Ingrese un teléfono válido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    
    if (!acceptsTerms) {
      newErrors.acceptsTerms = 'Debe aceptar los términos y condiciones';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Enviar formulario
  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const apiUrl = brandConfig.apiUrl;
      
      // Enviar datos al backend - crear cliente y notificación
      const response = await fetch(`${apiUrl}/notifications/project-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName || null,
          email: formData.email,
          phone: formData.phone,
          project_id: projectId,
          accepts_terms: acceptsTerms
        })
      });

      if (response.ok) {
        setIsSuccess(true);
        // Limpiar formulario
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: ''
        });
        setAcceptsTerms(false);
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.detail || 'Error al registrar. Por favor, intenta nuevamente.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Error de conexión. Por favor, intenta nuevamente.' });
    } finally {
      setIsSubmitting(false);
    }
  }
};

export default DraftProjectModal;

